import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, DatePicker, Input, Table, Spin, message } from "antd";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/zh_CN";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import empty_png from "@/images/empty.png";
import { commonApi, serviceApi } from "@/apis";
import NavTab from "./components/NavTab";
import { useUserInfo } from "@/model";
import "./index.less";

import "dayjs/locale/zh-cn";

const { RangePicker } = DatePicker;

const OrderList = () => {
  const userInfo = useUserInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  // 搜索条件
  const [orderNum, setOrderNum] = useState("");
  const [orderTime, setOrderTime] = useState<[any, any]>(["", ""]);
  const [curKey, setCurKey] = useState("");

  // 表格信息
  const [tableData, setTableData] = useState<OrderFindByConditionsItem[]>([]);
  const [curPage, setCurPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [orderSourceDict, setOrderSourceDict] = useState<{
    [key: string]: string;
  }>({}); // 下单方式
  const [orderStateDict, setOrderStateDict] = useState<{
    [key: string]: string;
  }>({}); // 订单状态

  // 获取字典
  useEffect(() => {
    const _state = location.state;
    setCurKey(_state || "");
    commonApi
      .getDictionaryList({
        dictTypeCodeList: ["order_source", "crm_order_state"],
      })
      .then((res) => {
        const order_source = res.order_source;
        const _orderSourceDict: any = {};
        order_source.forEach((el) => {
          _orderSourceDict[el.dictCode] = el.dictValue;
        });
        setOrderSourceDict(_orderSourceDict);
        const crm_order_state = res.crm_order_state;
        const _orderStateDict: any = {};
        crm_order_state.forEach((el) => {
          _orderStateDict[el.dictCode] = el.dictValue;
        });
        setOrderStateDict(_orderStateDict);
      })
      .finally(() => {
        if (_state) {
          handleSearch({
            orderStatus: _state,
          });
        } else {
          handleSearch();
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (data: any = {}) => {
    setLoading(true);
    serviceApi
      .getOrderFindByConditions({
        orderStatus: curKey,
        orderType: "ZOR1",
        cusCode: userInfo.curUserInfo?.customerCode || "",
        productGroupList: userInfo.curUserInfo?.productGroup || "",
        page: curPage,
        size: pageSize,
        bstnk: orderNum,
        startDate:
          orderTime && orderTime[0] && dayjs(orderTime[0]).format("YYYY-MM-DD"),
        endDate:
          orderTime && orderTime[1] && dayjs(orderTime[1]).format("YYYY-MM-DD"),
        ...data,
      })
      .then((res) => {
        setTableData(res.records);
        setTotal(res.total);
      })
      .catch((err) => {
        message.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReset = () => {
    setOrderNum("");
    setOrderTime(["", ""]);
    setCurPage(1);
    setPageSize(20);
    handleSearch({
      bstnk: "",
      startDate: "",
      endDate: "",
      page: 1,
      size: 20
    });
  };

  const changePage = (page: number, pageSize: number) => {
    setCurPage(page);
    setPageSize(pageSize);
    handleSearch({
      page,
      size: pageSize
    });
  };

  const changeTab = (key: string) => {
    setCurKey(key);
    setOrderNum("");
    setOrderTime(["", ""]);
    setCurPage(1);
    handleSearch({
      orderStatus: key,
      bstnk: "",
      startDate: "",
      endDate: "",
      page: 1,
    });
  };

  const handleInquire = () => {
    setCurPage(1);
    handleSearch({ page: 1 });
  };

  const goDetail = (item: OrderFindByConditionsItem) => {
    navigate(`/service/orderDetail?id=${item.id}`);
  };

  const goMoreOrder = (item: OrderFindByConditionsItem) => {
    navigate(`/quickOrder/dytOrder/${item.id}`);
  };

  const columns: ColumnsType<OrderFindByConditionsItem> = [
    {
      title: "订单编号",
      dataIndex: "bstnk",
      key: "bstnk",
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      render: (text, item) => <a onClick={() => goDetail(item)}>{text}</a>,
    },
    {
      title: "下单时间",
      dataIndex: "erdat",
      key: "erdat",
    },
    {
      title: "下单方式",
      dataIndex: "zsorc",
      key: "zsorc",
      render: (text) => {
        // “CRM后台下单”前台显示为“销售下单”；“TMS下单”前台显示为“主动配送”
        if (text === "B") return <span>销售下单</span>;
        if (text === "H") return <span>主动配送</span>;
        return <span>{orderSourceDict[text]}</span>;
      },
    },
    {
      title: "订单状态",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (text) => <span>{orderStateDict[text]}</span>,
    },
    {
      title: "操作",
      key: "action",
      render: (_, item) => {
        if (
          item.orderStatus &&
          ["waitShipped", "partShipped", "completed"].indexOf(
            item.orderStatus
          ) !== -1
        ) {
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          return <a onClick={() => goMoreOrder(item)}>再来一单</a>;
        }
        return null;
      },
    },
  ];

  return (
    <article className="order-list-wrap">
      <Spin spinning={loading}>
        <NavTab curKey={curKey} changeTab={changeTab} />
        <div className="order-table-wrap">
          <article className="flex-b-c search-article">
            <div className="flex">
              <Input
                value={orderNum}
                onChange={(e) => setOrderNum(e.target.value)}
                className="order-number"
                allowClear
                placeholder="请输入订单编号"
              />
              <RangePicker
                value={orderTime}
                onChange={(e: any) => setOrderTime(e)}
                locale={locale}
                className="order-time"
                placeholder={["发布开始时间", "发布结束时间"]}
              />
            </div>
            <div className="btns">
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                className="inquire-btn"
                icon={<SearchOutlined />}
                type="primary"
                onClick={handleInquire}
              >
                查询
              </Button>
            </div>
          </article>
          <Table
            columns={columns}
            dataSource={tableData}
            className="order-table"
            locale={{
              emptyText: (
                <div style={{ marginBottom: 30 }}>
                  <img src={empty_png} alt="暂无数据" />
                  <p>暂无数据</p>
                </div>
              ),
            }}
            pagination={{
              current: curPage,
              pageSize,
              total,
              defaultPageSize: 20,
              onChange: changePage,
            }}
          />
        </div>
      </Spin>
    </article>
  );
};

export default OrderList;
